import React, { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {
  const [currentSection, setCurrentSection] = useState(0);
  const [hasEntered, setHasEntered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [noButtonPosition, setNoButtonPosition] = useState({ x: 0, y: 0 });
  const [showHearts, setShowHearts] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const noButtonRef = useRef(null);
  const touchStartX = useRef(0);
  const resumeMusic = () => {
  if (audioRef.current && audioRef.current.paused) {
    audioRef.current.play().catch(() => {});
    setIsPlaying(true);
  }
};

  const sections = [
    { id: 0, component: 'intro' },
    { id: 1, component: 'warmth' },
    { id: 2, component: 'meaning' },
    { id: 3, component: 'moments' },
    { id: 4, component: 'proposal' },
    { id: 5, component: 'gratitude' },
    { id: 6, component: 'musicPlayer' },
    { id: 7, component: 'promises' },
    { id: 8, component: 'future' },
    { id: 9, component: 'ending' }
  ];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!hasEntered) return;
      if (e.key === 'ArrowRight') {
        if (currentSection < sections.length - 1) {
          setCurrentSection(currentSection + 1);
        }
      }
      if (e.key === 'ArrowLeft') {
        if (currentSection > 0) {
          setCurrentSection(currentSection - 1);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasEntered, currentSection, sections.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (!hasEntered) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        nextSection();
      } else {
        prevSection();
      }
    }
  };

  const enterSite = () => {
  if (audioRef.current) {
    audioRef.current.volume = volume;
    audioRef.current.play().catch(() => {});
    setIsPlaying(true);
  }

  // 🔥 AFTER audio starts, THEN enter site
  setHasEntered(true);
};

  const nextSection = () => {
	resumeMusic();
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const prevSection = () => {
	resumeMusic();
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const toggleMusic = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration || 0);
    }
  };

  const handleSeek = (e) => {
    const seekTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleTimeUpdate);
      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleTimeUpdate);
      };
    }
  }, []);

  const handleNoButtonHover = (e) => {
    const button = noButtonRef.current;
    if (!button) return;
    
    const maxX = window.innerWidth - button.offsetWidth - 20;
    const maxY = window.innerHeight - button.offsetHeight - 20;
    
    const newX = Math.random() * maxX;
    const newY = Math.random() * maxY;
    
    setNoButtonPosition({ x: newX, y: newY });
  };

  const handleYesClick = async () => {
    setShowHearts(true);
    
    // Send Discord webhook
    try {
      await fetch('https://discord.com/api/webhooks/1470824552467534017/N-RSayVY9dka2T2XmLQG4HJEoQvtMv5qX05006cFGa3CaJnXIJxnPNOp74TMSgLpxzE9', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: '💕 SHE SAID YES! She clicked the YES button! 💕',
          username: 'Valentine Proposal Bot'
        })
      });
    } catch (error) {
      console.log('Webhook failed:', error);
    }
    
    setTimeout(() => {
      setShowHearts(false);
      nextSection();
    }, 2500);
  };

  const createPetals = () => {
    return Array.from({ length: 30 }, (_, i) => (
      <div
        key={i}
        className="petal"
        style={{
          left: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 10}s`,
          animationDuration: `${8 + Math.random() * 8}s`,
          fontSize: `${12 + Math.random() * 16}px`,
          opacity: 0.3 + Math.random() * 0.4
        }}
      >
        🌸
      </div>
    ));
  };

  if (!hasEntered) {
    return (
      <div className="entry-screen">
        <div className="petals-container">{createPetals()}</div>
        <div className="entry-content">
          <h1 className="entry-title" data-testid="entry-title">A Journey of Love</h1>
          <p className="entry-subtitle">Close your eyes, take a breath, and let your heart feel</p>
          <button className="enter-button" onClick={enterSite} data-testid="enter-button">
            <span>Begin Our Journey</span>
            <div className="button-glow"></div>
          </button>
        </div>
        <audio ref={audioRef} loop>
          <source src="/music.mp3" type="audio/mpeg" />
        </audio>
      </div>
    );
  }

  return (
    <div 
      className="app-container" 
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      data-testid="main-app"
    >
      <div className="petals-container">{createPetals()}</div>
      
      {/* Section 0: Introduction */}
      <div className={`section ${currentSection === 0 ? 'active' : ''}`} data-testid="section-intro">
        <div className="glass-card">
          <h1 className="section-title heartbeat">Our Journey Begins</h1>
          <p className="section-text">
            In a world full of chaos and noise,<br />
            I found <span className="highlight">peace</span> in your voice.<br />
            Every moment with you feels like a <span className="highlight">dream</span>,<br />
            A beautiful story, more than it may seem.
          </p>
        </div>
      </div>

      {/* Section 1: Warmth */}
      <div className={`section ${currentSection === 1 ? 'active' : ''}`} data-testid="section-warmth">
        <div className="glass-card">
          <h1 className="section-title">You Are My <span className="highlight heartbeat">Warmth</span></h1>
          <p className="section-text">
            When the world feels cold and distant,<br />
            Your smile brings <span className="highlight">warmth</span> in an instant.<br />
            Like the first rays of morning sun,<br />
            You make my heart feel like it's won.<br /><br />
            Your presence is my <span className="highlight">comfort zone</span>,<br />
            With you, I never feel alone.
          </p>
        </div>
      </div>

      {/* Section 2: Meaning */}
      <div className={`section ${currentSection === 2 ? 'active' : ''}`} data-testid="section-meaning">
        <div className="glass-card">
          <h1 className="section-title">What You <span className="highlight heartbeat">Mean</span> to Me</h1>
          <p className="section-text">
            You are the rhythm in my heartbeat,<br />
            The reason my life feels <span className="highlight">complete</span>.<br />
            Every laugh, every word, every glance,<br />
            With you, life feels like a beautiful <span className="highlight">dance</span>.<br /><br />
            You're not just someone I adore,<br />
            You're everything I've been searching for.
          </p>
        </div>
      </div>

      {/* Section 3: Moments */}
      <div className={`section ${currentSection === 3 ? 'active' : ''}`} data-testid="section-moments">
        <div className="glass-card">
          <h1 className="section-title">Our <span className="highlight heartbeat">Special</span> Connection</h1>
          <p className="section-text">
            Every moment spent with you is a <span className="highlight">treasure</span>,<br />
            A memory I hold beyond measure.<br />
            Your kindness, your grace, your gentle soul,<br />
            Makes me feel happy, peaceful, and <span className="highlight">whole</span>.<br /><br />
            In your eyes, I see my future bright,<br />
            With you, everything feels just right.
          </p>
        </div>
      </div>

      {/* Section 4: Proposal */}
      <div className={`section ${currentSection === 4 ? 'active' : ''}`} data-testid="section-proposal">
        <div className="glass-card proposal-card">
          <h1 className="section-title proposal-title">Will You Be My <span className="highlight heartbeat">Valentine</span>?</h1>
          <p className="section-text proposal-text">
            I've written these words from the depths of my heart,<br />
            Because with you, I want a forever start.<br />
            So here's my question, simple and true...
          </p>
          <div className="proposal-buttons">
            <button 
              className="yes-button" 
              onClick={handleYesClick}
              data-testid="yes-button"
            >
              <span>YES! 💕</span>
              <div className="button-glow"></div>
            </button>
            <button 
              ref={noButtonRef}
              className="no-button" 
              onMouseEnter={handleNoButtonHover}
              onTouchStart={handleNoButtonHover}
              style={{
                position: 'fixed',
                left: `${noButtonPosition.x}px`,
                top: `${noButtonPosition.y}px`,
                transition: 'all 0.3s ease'
              }}
              data-testid="no-button"
            >
              No
            </button>
          </div>
        </div>
        {showHearts && (
          <div className="hearts-explosion" data-testid="hearts-explosion">
            {Array.from({ length: 30 }, (_, i) => (
              <div key={i} className="flying-heart" style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 0.5}s`
              }}>💕</div>
            ))}
          </div>
        )}
      </div>

      {/* Section 5: Gratitude */}
      <div className={`section ${currentSection === 5 ? 'active' : ''}`} data-testid="section-gratitude">
        <div className="glass-card">
          <h1 className="section-title">Thank You for <span className="highlight heartbeat">Saying Yes</span></h1>
          <p className="section-text">
            You've made me the <span className="highlight">happiest</span> person alive,<br />
            With you by my side, I truly thrive.<br />
            Thank you for choosing to be mine,<br />
            For making this moment so <span className="highlight">divine</span>.<br /><br />
            I promise to cherish every single day,<br />
            And love you more in every way.
          </p>
        </div>
      </div>

      {/* Section 6: Music Player */}
      <div className={`section ${currentSection === 6 ? 'active' : ''}`} data-testid="section-music">
        <div className="glass-card music-card">
          <h1 className="section-title">Our <span className="highlight heartbeat">Love</span> Song</h1>
          <div className="music-player">
            {/* Vinyl Disc Visual */}
            <div className="vinyl-container">
              <div className={`vinyl-disc ${isPlaying ? 'spinning' : ''}`}>
                <div className="vinyl-label">
                  <div className="vinyl-center"></div>
                  <div className="vinyl-text">♡</div>
                </div>
                <div className="vinyl-grooves"></div>
              </div>
              <div className="vinyl-arm"></div>
            </div>

            {/* Song Info */}
            <div className="song-info">
              <p className="song-title">Our Romantic Journey</p>
              <p className="song-artist">Made with Love</p>
            </div>

            {/* Progress Bar */}
            <div className="progress-container">
              <span className="time-display">{formatTime(currentTime)}</span>
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={handleSeek}
                className="progress-bar"
                data-testid="progress-bar"
              />
              <span className="time-display">{formatTime(duration)}</span>
            </div>

            {/* Controls */}
            <div className="music-controls">
              <button className="control-button skip-button" data-testid="skip-back">
                <span>⏮</span>
              </button>
              
              <button 
                className="control-button play-button" 
                onClick={toggleMusic}
                data-testid="music-toggle"
              >
                <span>{isPlaying ? '⏸' : '▶'}</span>
              </button>
              
              <button className="control-button skip-button" data-testid="skip-forward">
                <span>⏭</span>
              </button>
            </div>

            {/* Volume Control */}
            <div className="volume-container">
              <span className="volume-icon">🔊</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                className="volume-slider"
                data-testid="volume-slider"
              />
              <span className="volume-percentage">{Math.round(volume * 100)}%</span>
            </div>

            <p className="music-subtitle">A melody made for love</p>
          </div>
        </div>
      </div>

      {/* Section 7: Promises */}
      <div className={`section ${currentSection === 7 ? 'active' : ''}`} data-testid="section-promises">
        <div className="glass-card">
          <h1 className="section-title">My <span className="highlight heartbeat">Promises</span> to You</h1>
          <p className="section-text">
            I promise to <span className="highlight">hold your hand</span> through every storm,<br />
            To keep your heart forever warm.<br />
            I promise to make you <span className="highlight">smile</span> each day,<br />
            And chase all your worries away.<br /><br />
            I promise to be your constant support,<br />
            Your safe haven, your comfort fort.<br />
            With you, I'll build a love so <span className="highlight">true</span>,<br />
            Forever and always, it's me and you.
          </p>
        </div>
      </div>

      {/* Section 8: Future */}
      <div className={`section ${currentSection === 8 ? 'active' : ''}`} data-testid="section-future">
        <div className="glass-card">
          <h1 className="section-title">Our <span className="highlight heartbeat">Forever</span> Together</h1>
          <p className="section-text">
            I see a future painted in <span className="highlight">rose</span> and gold,<br />
            A love story that never grows old.<br />
            Adventures together, hand in hand,<br />
            Building dreams across this land.<br /><br />
            Late night talks and morning coffee,<br />
            Dancing in the rain, feeling <span className="highlight">free</span>.<br />
            Growing old but staying young at heart,<br />
            Together forever, never apart.
          </p>
        </div>
      </div>

      {/* Section 9: Ending */}
      <div className={`section ${currentSection === 9 ? 'active' : ''}`} data-testid="section-ending">
        <div className="ending-content">
          <div className="heart-container">
            <div className="big-heart">💕</div>
          </div>
          <p className="ending-text">
            This is not an ending,<br />
            but a beautiful <span className="highlight">beginning</span>...
          </p>
          <div className="signature">
            <p>Made with love by <span className="highlight">Dayal</span>,</p>
            <p>for his <span className="highlight">Pasinda Aurat</span> 💖</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      {currentSection > 0 && (
        <button className="nav-button nav-left" onClick={prevSection} data-testid="nav-prev">
          ←
        </button>
      )}
      {currentSection < sections.length - 1 && (
        <button className="nav-button nav-right" onClick={nextSection} data-testid="nav-next">
          →
        </button>
      )}

      {/* Progress indicator */}
      <div className="progress-indicator" data-testid="progress-indicator">
        {sections.map((_, index) => (
          <div
            key={index}
            className={`progress-dot ${currentSection === index ? 'active' : ''}`}
            onClick={() => setCurrentSection(index)}
          />
        ))}
      </div>
    </div>
  );
}

export default App;